import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatCLP, formatFecha, hoyISO, parseFecha, claveMes } from '../lib/format';
import { calcularFinanzas, ahorradoPorMeta, totalAbonadoPorCompromiso, gastoFijoPagado } from '../lib/finanzas';
import {
  q, crearMovimiento, recalcularCompromiso, recalcularMeta,
  eliminarMovimiento, actualizarMovimiento, buscarVinculos, borrarMovimientoSimple,
} from '../lib/vinculos';
import { useAccion } from '../lib/useAccion';
import { ivaDesdeTotal } from '../lib/iva';
import { gastadoPorPresupuesto, estadoPresupuesto } from '../lib/presupuestos';
import {
  PORCENTAJES_COMPARTIDO, personaCompartidaGuardada, parteCompartida, validarCompartido,
  personasConocidas,
} from '../lib/compartido';
import MovimientosPorMes from './MovimientosPorMes';
import { SelectRubro, CampoComprobante, ListaComprobantes } from './ComprobanteCampos';
import { subirComprobante, borrarComprobante, comprobantesDeMovimientos } from '../lib/comprobantes';
import { registrarMovimiento } from '../lib/registrar';
import ErrorBanner from './ErrorBanner';

const TIPOS = [
  { value: 'efectivo', label: 'Efectivo', icon: '💵' },
  { value: 'tarjeta', label: 'Tarjeta / Banco', icon: '💳' },
  { value: 'ahorro', label: 'Ahorro', icon: '🏦' },
  { value: 'otro', label: 'Otro', icon: '📂' },
];

const COLORES = [
  '#0891b2', '#0e7490', '#06b6d4', '#0284c7',
  '#7c3aed', '#059669', '#d97706', '#dc2626',
  '#db2777', '#64748b',
];

function getIconByTipo(tipo) {
  return TIPOS.find(t => t.value === tipo)?.icon || '📂';
}

const movimientoVacio = () => ({
  tipo: 'ingreso', monto: '', descripcion: '', fecha: hoyISO(), deuda_id: '', gasto_fijo_id: '', vincular: 'gasto',
  compartido: false, compartidoCon: personaCompartidaGuardada(), compartidoPct: 50,
  rubro: '', archivo: null, conFactura: false, presupuesto_id: '',
});

const abonoVacio = () => ({ monto: '', nota: '', fecha: hoyISO(), registrarSalida: true });

function Bolsillos({ userId }) {
  const [bolsillos, setBolsillos] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [compromisos, setCompromisos] = useState([]);
  const [abonos, setAbonos] = useState([]);
  const [metas, setMetas] = useState([]);
  const [abonosMeta, setAbonosMeta] = useState([]);
  const [deudas, setDeudas] = useState([]);
  const [gastosF, setGastosF] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [personasPrestamo, setPersonasPrestamo] = useState([]);
  const [loading, setLoading] = useState(true);
  const { saving, error, setError, run: ejecutar } = useAccion();

  // UI state
  const [expandedBolsillo, setExpandedBolsillo] = useState(null);
  const [showNewBolsillo, setShowNewBolsillo] = useState(false);
  const [showNewMovimiento, setShowNewMovimiento] = useState(null);
  const [showNewCompromiso, setShowNewCompromiso] = useState(null);
  const [editCompromiso, setEditCompromiso] = useState(null); // compromiso que se está editando
  const [cuadrar, setCuadrar] = useState(null); // { bolsilloId, saldoBanco }
  const [showAbono, setShowAbono] = useState(null);
  const [showAbonarMeta, setShowAbonarMeta] = useState(null); // bolsillo id
  const [editMov, setEditMov] = useState(null);
  const [editMovForm, setEditMovForm] = useState({ tipo: 'ingreso', monto: '', descripcion: '', fecha: '', rubro: '', archivo: null });
  const [comprobantesMov, setComprobantesMov] = useState([]);

  // Formularios
  const [formBolsillo, setFormBolsillo] = useState({ nombre: '', tipo: 'efectivo', color: '#0891b2' });
  const [formMovimiento, setFormMovimiento] = useState(movimientoVacio());
  const [formCompromiso, setFormCompromiso] = useState({ nombre: '', monto_total: '', descripcion: '', fecha: hoyISO() });
  const [formAbono, setFormAbono] = useState(abonoVacio());
  const [formAbonarMeta, setFormAbonarMeta] = useState({ meta_id: '', monto: '' });

  const loadData = useCallback(async () => {
    if (!userId) return;
    try {
      const [b, m, c, a, mt, am, deu, gf, personas, comps, presups] = await Promise.all([
        q(supabase.from('bolsillos').select('*').eq('user_id', userId).order('created_at')),
        q(supabase.from('bolsillo_movimientos').select('*').eq('user_id', userId).order('fecha')),
        q(supabase.from('compromisos').select('*').eq('user_id', userId).order('created_at')),
        q(supabase.from('abonos').select('*').eq('user_id', userId).order('fecha')),
        q(supabase.from('metas').select('*').eq('user_id', userId).order('created_at')),
        q(supabase.from('abonos_meta').select('*').eq('user_id', userId)),
        q(supabase.from('deudas').select('*').eq('user_id', userId).eq('activa', true).order('created_at')),
        q(supabase.from('gastos_fijos').select('*').eq('user_id', userId).order('created_at')),
        personasConocidas(userId),
        comprobantesDeMovimientos(userId),
        q(supabase.from('presupuestos').select('*').eq('user_id', userId).eq('activo', true).order('created_at')),
      ]);
      setBolsillos(b || []);
      setMovimientos(m || []);
      setCompromisos(c || []);
      setAbonos(a || []);
      setMetas(mt || []);
      setAbonosMeta(am || []);
      setDeudas(deu || []);
      setGastosF(gf || []);
      setPersonasPrestamo(personas);
      setComprobantesMov(comps || []);
      setPresupuestos(presups || []);
    } catch (e) {
      setError('Error cargando datos: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [userId, setError]);

  useEffect(() => { loadData(); }, [loadData]);

  // Guarda en la base, muestra el error si falla y recarga los datos.
  const run = (accion) => ejecutar(accion, loadData);

  // --- Cálculos (compartidos con Dashboard, Simulador y Billuyo) ---
  const fin = calcularFinanzas({ bolsillos, movimientos, compromisos, abonos, abonosMeta });
  const abonadoPorComp = totalAbonadoPorCompromiso(abonos);
  const ahorrado = ahorradoPorMeta(abonosMeta);
  const metasActivas = metas.filter(m => !m.completada);

  const getTotalAbonado = (compromisoId) => abonadoPorComp[compromisoId] || 0;
  const getPendienteCompromiso = (c) => Math.max(0, (c.monto_total || 0) - getTotalAbonado(c.id));
  const getDisponibleBolsillo = (id) => fin.porBolsillo(id).disponible;

  // --- Categorías ---
  const handleAddBolsillo = async (e) => {
    e.preventDefault();
    if (!formBolsillo.nombre.trim()) return;
    const ok = await run(() => q(supabase.from('bolsillos').insert({
      user_id: userId,
      nombre: formBolsillo.nombre.trim(),
      tipo: formBolsillo.tipo,
      color: formBolsillo.color,
    })));
    if (ok) {
      setFormBolsillo({ nombre: '', tipo: 'efectivo', color: '#0891b2' });
      setShowNewBolsillo(false);
    }
  };

  const handleDeleteBolsillo = async (id) => {
    if (!window.confirm('¿Eliminar esta categoría y todos sus movimientos y compromisos?')) return;
    const ok = await run(() => q(supabase.from('bolsillos').delete().eq('id', id)));
    if (ok && expandedBolsillo === id) setExpandedBolsillo(null);
  };

  // Categoría aparte: su plata no se suma al saldo total ni al disponible (ej. ahorro en efectivo)
  const handleCambiarAparte = (b) => run(() => q(supabase.from('bolsillos').update({ aparte: !b.aparte }).eq('id', b.id)));

  // --- Movimientos ---
  const cerrarNuevoMovimiento = () => { setShowNewMovimiento(null); setFormMovimiento(movimientoVacio()); };

  // Cuadrar con el banco: un solo movimiento de ajuste deja el saldo igual al que dice el banco hoy.
  const handleCuadrar = async (e) => {
    e.preventDefault();
    const saldoBanco = parseFloat(cuadrar.saldoBanco);
    if (Number.isNaN(saldoBanco)) { setError('Escribe el saldo que muestra tu banco.'); return; }
    const diferencia = Math.round(saldoBanco - fin.porBolsillo(cuadrar.bolsilloId).saldo);
    if (diferencia === 0) { setCuadrar(null); return; }
    const ok = await run(() => crearMovimiento({
      userId,
      bolsilloId: cuadrar.bolsilloId,
      tipo: diferencia > 0 ? 'ingreso' : 'egreso',
      monto: Math.abs(diferencia),
      descripcion: 'Ajuste para cuadrar con el banco',
      fecha: hoyISO(),
      clase: 'ajuste', // no es ingreso ni gasto: Mi Día no lo cuenta
    }));
    if (ok) setCuadrar(null);
  };

  const handleAddMovimiento = async (e) => {
    e.preventDefault();
    if (!formMovimiento.monto || !showNewMovimiento) return;
    if (formMovimiento.vincular === 'deuda' && !formMovimiento.deuda_id) {
      setError('Selecciona la deuda a la que corresponde este pago.');
      return;
    }
    if (formMovimiento.vincular === 'gasto_fijo' && !formMovimiento.gasto_fijo_id) {
      setError('Selecciona el gasto fijo que estás pagando.');
      return;
    }
    const monto = parseFloat(formMovimiento.monto);
    const esEgreso = formMovimiento.tipo === 'egreso';
    const compartido = esEgreso && formMovimiento.vincular !== 'deuda' && formMovimiento.compartido;
    if (compartido) {
      const err = validarCompartido({ persona: formMovimiento.compartidoCon, monto, pct: formMovimiento.compartidoPct });
      if (err) { setError(err); return; }
    }
    const deuda = (esEgreso && formMovimiento.vincular === 'deuda') ? deudas.find(d => d.id === formMovimiento.deuda_id) : null;
    const gastoFijo = (esEgreso && formMovimiento.vincular === 'gasto_fijo') ? gastosF.find(g => g.id === formMovimiento.gasto_fijo_id) : null;

    // Descripción automática
    let descripcion = formMovimiento.descripcion || null;
    if (deuda && !descripcion) descripcion = `Pago deuda: ${deuda.nombre}`;
    if (gastoFijo && !descripcion) descripcion = `Gasto fijo: ${gastoFijo.nombre}`;

    const ok = await run(() => registrarMovimiento({
      userId,
      bolsilloId: showNewMovimiento,
      tipo: formMovimiento.tipo,
      monto,
      descripcion,
      fecha: formMovimiento.fecha,
      rubro: formMovimiento.rubro || null,
      conFactura: formMovimiento.conFactura,
      presupuestoId: formMovimiento.vincular === 'gasto' ? formMovimiento.presupuesto_id : null,
      deuda,
      gastoFijo,
      compartido: compartido ? { persona: formMovimiento.compartidoCon, pct: formMovimiento.compartidoPct } : null,
      archivo: formMovimiento.archivo,
    }));
    if (ok) cerrarNuevoMovimiento();
  };

  const handleDeleteMovimiento = async (mov) => {
    let v;
    try { v = await buscarVinculos(mov.id); } catch (e) { setError(e.message); return; }
    const avisos = [];
    if (v.pagos.length) avisos.push('Es un pago de deuda: también se eliminará de Mis Deudas.');
    if (v.cobros.length) avisos.push('Es un cobro de préstamo: también se eliminará de Me Deben.');
    if (v.abonos.length) avisos.push('Es un abono a un compromiso: el abono también se eliminará.');
    if (v.compras.length) avisos.push('Está vinculado a una compra: la compra se mantiene en Mis Compras.');
    if (v.ventas.length) avisos.push('Está vinculado a una venta: la venta se mantiene en Mi Negocio.');
    if (mov.gasto_fijo_id) avisos.push('Es el pago de un gasto fijo: volverá a quedar pendiente.');
    if (!window.confirm(['¿Eliminar este movimiento?', ...avisos].join('\n\n'))) return;
    await run(() => eliminarMovimiento(mov));
  };

  const handleStartEditMov = (mov) => {
    setEditMov(mov);
    setEditMovForm({ tipo: mov.tipo, monto: mov.monto, descripcion: mov.descripcion || '', fecha: mov.fecha, rubro: mov.rubro || '', archivo: null });
  };

  const handleSaveEditMov = async (e) => {
    e.preventDefault();
    const ok = await run(async () => {
      await actualizarMovimiento(editMov, {
        tipo: editMovForm.tipo,
        monto: parseFloat(editMovForm.monto),
        descripcion: editMovForm.descripcion || null,
        fecha: editMovForm.fecha,
        rubro: editMovForm.tipo === 'egreso' ? (editMovForm.rubro || null) : null,
      });
      if (editMovForm.archivo) {
        await subirComprobante({
          userId, file: editMovForm.archivo, fecha: editMovForm.fecha, monto: parseFloat(editMovForm.monto),
          concepto: editMovForm.descripcion || 'Movimiento', rubro: editMovForm.rubro || null,
          gastoFijoId: editMov.gasto_fijo_id || null, movimientoId: editMov.id,
        });
      }
    });
    if (ok) setEditMov(null);
  };

  // --- Abonar a Meta desde una categoría ---
  // El dinero sigue en la categoría: no se crea egreso, solo baja el disponible.
  const handleAbonarMeta = async (e) => {
    e.preventDefault();
    if (!formAbonarMeta.meta_id || !formAbonarMeta.monto || !showAbonarMeta) return;
    const monto = parseFloat(formAbonarMeta.monto);
    const disponible = getDisponibleBolsillo(showAbonarMeta);
    if (monto > disponible) {
      setError(`No tienes suficiente disponible. Disponible: ${formatCLP(disponible)}`);
      return;
    }
    const ok = await run(async () => {
      await q(supabase.from('abonos_meta').insert({
        user_id: userId,
        meta_id: formAbonarMeta.meta_id,
        bolsillo_id: showAbonarMeta,
        monto,
        fecha: hoyISO(),
      }));
      await recalcularMeta(formAbonarMeta.meta_id);
    });
    if (ok) {
      setFormAbonarMeta({ meta_id: '', monto: '' });
      setShowAbonarMeta(null);
    }
  };

  // --- Compromisos ---
  const cerrarCompromiso = () => {
    setShowNewCompromiso(null);
    setEditCompromiso(null);
    setFormCompromiso({ nombre: '', monto_total: '', descripcion: '', fecha: hoyISO() });
  };

  const handleStartEditCompromiso = (comp) => {
    setEditCompromiso(comp);
    setFormCompromiso({ nombre: comp.nombre, monto_total: comp.monto_total, descripcion: comp.descripcion || '', fecha: comp.fecha || hoyISO() });
  };

  const handleAddCompromiso = async (e) => {
    e.preventDefault();
    if (!formCompromiso.nombre.trim() || !formCompromiso.monto_total) return;
    if (!showNewCompromiso && !editCompromiso) return;
    const datos = {
      nombre: formCompromiso.nombre.trim(),
      monto_total: parseFloat(formCompromiso.monto_total),
      descripcion: formCompromiso.descripcion || null,
      fecha: formCompromiso.fecha,
    };
    const ok = await run(async () => {
      if (editCompromiso) {
        await q(supabase.from('compromisos').update(datos).eq('id', editCompromiso.id));
        // Si cambió el total, puede pasar de pendiente a pagado (o al revés)
        await recalcularCompromiso(editCompromiso.id);
      } else {
        await q(supabase.from('compromisos').insert({
          ...datos,
          user_id: userId,
          bolsillo_id: showNewCompromiso,
          estado: 'pendiente',
        }));
      }
    });
    if (ok) cerrarCompromiso();
  };

  const handleDeleteCompromiso = async (id) => {
    if (!window.confirm('¿Eliminar este compromiso? Las salidas de dinero ya registradas por sus abonos se mantienen.')) return;
    await run(() => q(supabase.from('compromisos').delete().eq('id', id)));
  };

  // --- Abonos a compromisos ---
  const handleAddAbono = async (e) => {
    e.preventDefault();
    if (!formAbono.monto || !showAbono) return;
    const compromiso = compromisos.find(c => c.id === showAbono);
    if (!compromiso) return;
    const monto = Math.min(parseFloat(formAbono.monto), getPendienteCompromiso(compromiso));
    const ok = await run(async () => {
      // Si pagaste de verdad, se descuenta de la categoría en el mismo paso
      const mov = formAbono.registrarSalida
        ? await crearMovimiento({
            userId,
            bolsilloId: compromiso.bolsillo_id,
            tipo: 'egreso',
            monto,
            descripcion: `Pago compromiso: ${compromiso.nombre}${formAbono.nota ? ` · ${formAbono.nota}` : ''}`,
            fecha: formAbono.fecha,
          })
        : null;
      try {
        await q(supabase.from('abonos').insert({
          user_id: userId,
          compromiso_id: compromiso.id,
          movimiento_id: mov?.id || null,
          monto,
          nota: formAbono.nota || null,
          fecha: formAbono.fecha,
        }));
      } catch (err) {
        await borrarMovimientoSimple(mov?.id);
        throw err;
      }
      await recalcularCompromiso(compromiso.id);
    });
    if (ok) {
      setFormAbono(abonoVacio());
      setShowAbono(null);
    }
  };

  const handleDeleteAbono = async (abono) => {
    const msg = abono.movimiento_id
      ? '¿Eliminar este abono? También se eliminará la salida de dinero que se registró con él.'
      : '¿Eliminar este abono?';
    if (!window.confirm(msg)) return;
    await run(async () => {
      await q(supabase.from('abonos').delete().eq('id', abono.id));
      await borrarMovimientoSimple(abono.movimiento_id);
      await recalcularCompromiso(abono.compromiso_id);
    });
  };

  // --- Render ---
  if (loading) {
    return (
      <div className="bolsillos-loading">
        <div className="loading-spinner"></div>
        <p>Cargando tus categorías...</p>
      </div>
    );
  }

  const { totalSaldo, totalComprometido, totalEnMetas, totalDisponible, enMetasSinCategoria, enRojo: bolsillosEnRojo } = fin;

  return (
    <div className="bolsillos-page">
      <ErrorBanner error={error} onClose={() => setError('')} />

      {bolsillosEnRojo.length > 0 && (
        <div className="alerta-rojo">
          <span className="alerta-rojo-icon">⚠️</span>
          <div>
            <strong>Categoría{bolsillosEnRojo.length > 1 ? 's' : ''} en rojo:</strong>{' '}
            {bolsillosEnRojo.map(b => b.nombre).join(', ')}.{' '}
            Tu disponible real es negativo — tienes más compromisos y metas que saldo.
          </div>
        </div>
      )}

      <div className="page-header">
        <div>
          <h2>Mis Categorías</h2>
          <p className="page-subtitle">Organiza tu dinero por categoría y controla tus compromisos</p>
        </div>
        <button className="btn-primary" onClick={() => setShowNewBolsillo(true)} disabled={saving}>
          + Nueva categoría
        </button>
      </div>

      <div className="bolsillos-resumen">
        <div className="resumen-card resumen-total">
          <span className="resumen-label">Saldo total</span>
          <span className="resumen-value">{formatCLP(totalSaldo)}</span>
          <span className="resumen-hint">Lo que tienes en el banco</span>
        </div>
        <div className="resumen-card resumen-comprometido">
          <span className="resumen-label">Comprometido</span>
          <span className="resumen-value text-warning">{formatCLP(totalComprometido)}</span>
          <span className="resumen-hint">Reservado para gastos pendientes</span>
        </div>
        {totalEnMetas > 0 && (
          <div className="resumen-card resumen-comprometido">
            <span className="resumen-label">En metas</span>
            <span className="resumen-value" style={{ color: 'var(--primary)' }}>{formatCLP(totalEnMetas)}</span>
            <span className="resumen-hint">
              Reservado para tus ahorros
              {enMetasSinCategoria > 0 && ` (${formatCLP(enMetasSinCategoria)} sin categoría asignada)`}
            </span>
          </div>
        )}
        <div className="resumen-card resumen-disponible">
          <span className="resumen-label">Disponible real</span>
          <span className={`resumen-value ${totalDisponible >= 0 ? 'text-success' : 'text-danger'}`}>
            {formatCLP(totalDisponible)}
          </span>
          <span className="resumen-hint">Lo que puedes gastar libremente</span>
        </div>
      </div>

      {/* Modal: Nueva categoría */}
      {showNewBolsillo && (
        <div className="modal-overlay" onClick={() => setShowNewBolsillo(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Crear nueva categoría</h3>
              <button className="modal-close" onClick={() => setShowNewBolsillo(false)}>×</button>
            </div>
            <form onSubmit={handleAddBolsillo}>
              <div className="form-group">
                <label>Nombre</label>
                <input type="text" value={formBolsillo.nombre}
                  onChange={e => setFormBolsillo({ ...formBolsillo, nombre: e.target.value })}
                  placeholder="ej: Efectivo, Banco BCI, Ahorro viaje..." required autoFocus />
              </div>
              <div className="form-group">
                <label>Tipo</label>
                <div className="tipo-selector">
                  {TIPOS.map(t => (
                    <button key={t.value} type="button"
                      className={`tipo-btn ${formBolsillo.tipo === t.value ? 'active' : ''}`}
                      onClick={() => setFormBolsillo({ ...formBolsillo, tipo: t.value })}>
                      <span>{t.icon}</span><span>{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>Color</label>
                <div className="color-selector">
                  {COLORES.map(c => (
                    <button key={c} type="button"
                      className={`color-dot ${formBolsillo.color === c ? 'active' : ''}`}
                      style={{ background: c }}
                      onClick={() => setFormBolsillo({ ...formBolsillo, color: c })} />
                  ))}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowNewBolsillo(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Guardando...' : 'Crear categoría'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Nuevo movimiento */}
      {showNewMovimiento && (
        <div className="modal-overlay" onClick={cerrarNuevoMovimiento}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Registrar movimiento</h3>
              <button className="modal-close" onClick={cerrarNuevoMovimiento}>×</button>
            </div>
            <form onSubmit={handleAddMovimiento}>
              <div className="form-group">
                <label>Tipo</label>
                <div className="tipo-toggle">
                  <button type="button" className={`toggle-btn ${formMovimiento.tipo === 'ingreso' ? 'active-green' : ''}`}
                    onClick={() => setFormMovimiento({ ...formMovimiento, tipo: 'ingreso', vincular: '', deuda_id: '', gasto_fijo_id: '' })}>Entrada de dinero</button>
                  <button type="button" className={`toggle-btn ${formMovimiento.tipo === 'egreso' ? 'active-red' : ''}`}
                    onClick={() => setFormMovimiento({ ...formMovimiento, tipo: 'egreso', vincular: 'gasto', deuda_id: '', gasto_fijo_id: '' })}>Salida de dinero</button>
                </div>
              </div>
              <div className="form-group">
                <label>Monto ($)</label>
                <input type="number" value={formMovimiento.monto}
                  onChange={e => setFormMovimiento({ ...formMovimiento, monto: e.target.value })}
                  placeholder="0" min="1" required autoFocus />
              </div>
              {formMovimiento.tipo === 'egreso' && (
                <div className="form-group">
                  <label>¿A qué corresponde esta salida?</label>
                  <div className="tipo-toggle" style={{ flexWrap: 'wrap' }}>
                    <button type="button"
                      className={`toggle-btn ${formMovimiento.vincular === 'gasto' ? 'active-red' : ''}`}
                      onClick={() => setFormMovimiento({ ...formMovimiento, vincular: 'gasto', deuda_id: '', gasto_fijo_id: '' })}>
                      🛒 Gasto
                    </button>
                    {gastosF.length > 0 && (
                      <button type="button"
                        className={`toggle-btn ${formMovimiento.vincular === 'gasto_fijo' ? 'active-red' : ''}`}
                        onClick={() => setFormMovimiento({ ...formMovimiento, vincular: 'gasto_fijo', deuda_id: '' })}>
                        🏠 Gasto fijo
                      </button>
                    )}
                    {deudas.length > 0 && (
                      <button type="button"
                        className={`toggle-btn ${formMovimiento.vincular === 'deuda' ? 'active-red' : ''}`}
                        onClick={() => setFormMovimiento({ ...formMovimiento, vincular: 'deuda', gasto_fijo_id: '' })}>
                        💳 Deuda
                      </button>
                    )}
                  </div>
                  {formMovimiento.vincular === 'gasto_fijo' && (() => {
                    const pendientesGF = gastosF.filter(g => !gastoFijoPagado(g, parseFecha(formMovimiento.fecha) || new Date()));
                    return pendientesGF.length > 0 ? (
                      <select style={{ marginTop: 8 }} value={formMovimiento.gasto_fijo_id}
                        onChange={e => {
                          const g = gastosF.find(x => x.id === e.target.value);
                          setFormMovimiento({
                            ...formMovimiento, gasto_fijo_id: e.target.value,
                            monto: formMovimiento.monto || (g ? g.monto : ''),
                            rubro: formMovimiento.rubro || g?.rubro || '',
                            ...(g?.compartido_con ? { compartido: true, compartidoCon: g.compartido_con, compartidoPct: g.compartido_pct || 50 } : {}),
                          });
                        }}>
                        <option value="">Selecciona el gasto fijo...</option>
                        {pendientesGF.map(g => (
                          <option key={g.id} value={g.id}>🏠 {g.nombre} — {formatCLP(g.monto)}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="field-hint" style={{ marginTop: 8 }}>Todos los gastos fijos ya están pagados en ese período.</p>
                    );
                  })()}
                  {formMovimiento.vincular === 'deuda' && (
                    <select style={{ marginTop: 8 }} value={formMovimiento.deuda_id}
                      onChange={e => setFormMovimiento({ ...formMovimiento, deuda_id: e.target.value })}>
                      <option value="">Selecciona la deuda...</option>
                      {deudas.map(d => (
                        <option key={d.id} value={d.id}>{d.icono || '💳'} {d.nombre}{d.acreedor ? ` (${d.acreedor})` : ''}</option>
                      ))}
                    </select>
                  )}
                  {formMovimiento.vincular === 'gasto_fijo' && formMovimiento.gasto_fijo_id && (
                    <span className="field-hint">✅ Se marcará como pagado en Gastos Fijos</span>
                  )}
                  {formMovimiento.vincular === 'deuda' && formMovimiento.deuda_id && (
                    <span className="field-hint">✅ Se registrará en Mis Deudas automáticamente</span>
                  )}
                </div>
              )}
              {formMovimiento.tipo === 'egreso' && formMovimiento.vincular !== 'deuda' && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={formMovimiento.compartido}
                      onChange={e => setFormMovimiento({ ...formMovimiento, compartido: e.target.checked })}
                      style={{ width: 18, height: 18, accentColor: 'var(--primary)' }} />
                    👥 Gasto compartido (alguien me debe una parte)
                  </label>
                  {formMovimiento.compartido && (
                    <>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <input type="text" list="personas-compartido" style={{ flex: 2 }}
                          value={formMovimiento.compartidoCon}
                          onChange={e => setFormMovimiento({ ...formMovimiento, compartidoCon: e.target.value })}
                          placeholder="¿Con quién? ej: Esposa" />
                        <datalist id="personas-compartido">
                          {personasPrestamo.map(p => <option key={p} value={p} />)}
                        </datalist>
                        <select style={{ flex: 1 }} value={formMovimiento.compartidoPct}
                          onChange={e => setFormMovimiento({ ...formMovimiento, compartidoPct: Number(e.target.value) })}>
                          {PORCENTAJES_COMPARTIDO.map(p => <option key={p} value={p}>{p}%</option>)}
                        </select>
                      </div>
                      <span className="field-hint">
                        ✅ Sale {formatCLP(Number(formMovimiento.monto) || 0)} de tu categoría y
                        {' '}{formMovimiento.compartidoCon.trim() || 'esa persona'} te queda debiendo
                        {' '}{formatCLP(parteCompartida(formMovimiento.monto, formMovimiento.compartidoPct))} en Me Deben.
                      </span>
                    </>
                  )}
                </div>
              )}
              <div className="form-group">
                <label>Descripción {(formMovimiento.vincular === 'deuda' || formMovimiento.vincular === 'gasto_fijo') ? '(opcional)' : ''}</label>
                <input type="text" value={formMovimiento.descripcion}
                  onChange={e => setFormMovimiento({ ...formMovimiento, descripcion: e.target.value })}
                  placeholder={(formMovimiento.vincular === 'deuda' || formMovimiento.vincular === 'gasto_fijo') ? 'Se autocompleta — puedes agregar nota' : 'ej: Supermercado, Farmacia...'} />
              </div>
              <div className="form-group">
                <label>Fecha</label>
                <input type="date" value={formMovimiento.fecha}
                  onChange={e => setFormMovimiento({ ...formMovimiento, fecha: e.target.value })} />
              </div>
              {formMovimiento.tipo === 'egreso' && (
                <>
                  <div className="form-group">
                    <label>Rubro</label>
                    <SelectRubro value={formMovimiento.rubro} onChange={v => setFormMovimiento({ ...formMovimiento, rubro: v })} />
                  </div>
                  {formMovimiento.vincular === 'gasto' && presupuestos.length > 0 && (
                    <div className="form-group">
                      <label>¿De qué presupuesto sale?</label>
                      <select value={formMovimiento.presupuesto_id}
                        onChange={e => setFormMovimiento({ ...formMovimiento, presupuesto_id: e.target.value })}>
                        <option value="">Sin presupuesto</option>
                        {presupuestos.map(p => (
                          <option key={p.id} value={p.id}>{p.icono || '🧺'} {p.nombre}</option>
                        ))}
                      </select>
                      {formMovimiento.presupuesto_id && (() => {
                        const pres = presupuestos.find(x => x.id === formMovimiento.presupuesto_id);
                        if (!pres) return null;
                        const yaGastado = gastadoPorPresupuesto(movimientos, claveMes(formMovimiento.fecha))[pres.id] || 0;
                        const est = estadoPresupuesto(pres, yaGastado);
                        const esteGasto = parseFloat(formMovimiento.monto) || 0;
                        const quedaria = est.restante - esteGasto;
                        return (
                          <div style={{ marginTop: 6 }}>
                            <div style={{ background: 'var(--border)', borderRadius: 99, height: 8, overflow: 'hidden' }}>
                              <div style={{ width: `${Math.min(100, est.pct)}%`, height: '100%', background: est.color, borderRadius: 99, transition: 'width 0.3s' }} />
                            </div>
                            <span className="field-hint">
                              Llevas {formatCLP(est.usado)} de {formatCLP(est.tope)}
                              {esteGasto > 0 && (quedaria >= 0
                                ? ` · con este gasto te quedan ${formatCLP(quedaria)}`
                                : ` · con este gasto te pasas por ${formatCLP(Math.abs(quedaria))}`)}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 400 }}>
                      <input type="checkbox" checked={formMovimiento.conFactura}
                        onChange={e => setFormMovimiento({ ...formMovimiento, conFactura: e.target.checked })}
                        style={{ width: 18, height: 18, accentColor: 'var(--primary)' }} />
                      Me dieron factura
                    </label>
                    {formMovimiento.conFactura && Number(formMovimiento.monto) > 0 && (
                      <span className="field-hint">
                        IVA de este gasto: {formatCLP(ivaDesdeTotal(Number(formMovimiento.monto)))} — suma a tu IVA crédito en Mi Negocio
                      </span>
                    )}
                  </div>
                </>
              )}
              <CampoComprobante archivo={formMovimiento.archivo} onChange={f => setFormMovimiento({ ...formMovimiento, archivo: f })} />
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={cerrarNuevoMovimiento}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Registrar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Editar movimiento */}
      {editMov && (
        <div className="modal-overlay" onClick={() => setEditMov(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Editar movimiento</h3>
              <button className="modal-close" onClick={() => setEditMov(null)}>×</button>
            </div>
            <form onSubmit={handleSaveEditMov}>
              <div className="form-group">
                <label>Tipo</label>
                <div className="tipo-toggle">
                  <button type="button" className={`toggle-btn ${editMovForm.tipo === 'ingreso' ? 'active-green' : ''}`}
                    onClick={() => setEditMovForm({ ...editMovForm, tipo: 'ingreso' })}>Entrada</button>
                  <button type="button" className={`toggle-btn ${editMovForm.tipo === 'egreso' ? 'active-red' : ''}`}
                    onClick={() => setEditMovForm({ ...editMovForm, tipo: 'egreso' })}>Salida</button>
                </div>
              </div>
              <div className="form-group">
                <label>Monto ($)</label>
                <input type="number" value={editMovForm.monto}
                  onChange={e => setEditMovForm({ ...editMovForm, monto: e.target.value })}
                  required min="1" autoFocus />
                <span className="field-hint">Si es un pago de deuda, cobro o abono, el cambio se aplica también allá.</span>
              </div>
              <div className="form-group">
                <label>Descripción</label>
                <input type="text" value={editMovForm.descripcion}
                  onChange={e => setEditMovForm({ ...editMovForm, descripcion: e.target.value })}
                  placeholder="Descripción del movimiento" />
              </div>
              <div className="form-group">
                <label>Fecha</label>
                <input type="date" value={editMovForm.fecha}
                  onChange={e => setEditMovForm({ ...editMovForm, fecha: e.target.value })} />
              </div>
              {editMovForm.tipo === 'egreso' && (
                <div className="form-group">
                  <label>Rubro</label>
                  <SelectRubro value={editMovForm.rubro} onChange={v => setEditMovForm({ ...editMovForm, rubro: v })} />
                </div>
              )}
              <ListaComprobantes
                comprobantes={comprobantesMov.filter(c => c.movimiento_id === editMov.id)}
                onError={setError}
                onBorrar={(c) => window.confirm('¿Quitar este comprobante? El archivo se borra.') && run(() => borrarComprobante(c))}
              />
              <CampoComprobante archivo={editMovForm.archivo} onChange={f => setEditMovForm({ ...editMovForm, archivo: f })}
                label="📎 Adjuntar comprobante" />
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setEditMov(null)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Guardar cambios'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Abonar a Meta */}
      {showAbonarMeta && (
        <div className="modal-overlay" onClick={() => setShowAbonarMeta(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🎯 Abonar a una Meta</h3>
              <button className="modal-close" onClick={() => setShowAbonarMeta(null)}>×</button>
            </div>
            {metasActivas.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)' }}>
                <p>No tienes metas activas.</p>
                <p style={{ fontSize: '0.85rem' }}>Ve a "Mis Metas" y crea una primero.</p>
                <button className="btn-secondary" style={{ marginTop: 12 }} onClick={() => setShowAbonarMeta(null)}>Cerrar</button>
              </div>
            ) : (
              <>
                <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  El dinero sigue en esta categoría, pero queda reservado para tu meta: baja tu disponible, no tu saldo.
                </p>
                <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  Disponible en esta categoría: <strong style={{ color: 'var(--primary)' }}>
                    {formatCLP(getDisponibleBolsillo(showAbonarMeta))}
                  </strong>
                </p>
                <form onSubmit={handleAbonarMeta}>
                  <div className="form-group">
                    <label>¿A qué meta quieres abonar?</label>
                    <select value={formAbonarMeta.meta_id}
                      onChange={e => setFormAbonarMeta({ ...formAbonarMeta, meta_id: e.target.value })}
                      required>
                      <option value="">Selecciona una meta...</option>
                      {metasActivas.map(m => {
                        const actual = ahorrado[m.id] || 0;
                        const pct = m.monto_objetivo > 0 ? Math.round((actual / m.monto_objetivo) * 100) : 0;
                        return (
                          <option key={m.id} value={m.id}>
                            {m.icono} {m.nombre} — {pct}% completado ({formatCLP(actual)} / {formatCLP(m.monto_objetivo)})
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Monto a abonar ($)</label>
                    <input type="number" value={formAbonarMeta.monto}
                      onChange={e => setFormAbonarMeta({ ...formAbonarMeta, monto: e.target.value })}
                      placeholder="0" min="1"
                      max={getDisponibleBolsillo(showAbonarMeta)}
                      required autoFocus />
                    <span className="field-hint">Máximo disponible: {formatCLP(getDisponibleBolsillo(showAbonarMeta))}</span>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={() => setShowAbonarMeta(null)}>Cancelar</button>
                    <button type="submit" className="btn-primary" disabled={saving}>
                      {saving ? 'Guardando...' : '🎯 Abonar a meta'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal: Nuevo / editar compromiso */}
      {(showNewCompromiso || editCompromiso) && (
        <div className="modal-overlay" onClick={cerrarCompromiso}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editCompromiso ? 'Editar compromiso' : 'Agregar compromiso de gasto'}</h3>
              <button className="modal-close" onClick={cerrarCompromiso}>×</button>
            </div>
            <p className="modal-description">
              Un compromiso es dinero que ya está "reservado" para un gasto futuro.
              Se descuenta de tu disponible real aunque aún no lo hayas pagado.
            </p>
            <form onSubmit={handleAddCompromiso}>
              <div className="form-group">
                <label>Nombre del gasto</label>
                <input type="text" value={formCompromiso.nombre}
                  onChange={e => setFormCompromiso({ ...formCompromiso, nombre: e.target.value })}
                  placeholder="ej: Arriendo, Odontólogo, Seguro..." required autoFocus />
              </div>
              <div className="form-group">
                <label>Monto total a pagar ($)</label>
                <input type="number" value={formCompromiso.monto_total}
                  onChange={e => setFormCompromiso({ ...formCompromiso, monto_total: e.target.value })}
                  placeholder="0" min="1" required />
                {editCompromiso && getTotalAbonado(editCompromiso.id) > 0 && (
                  <span className="field-hint">Ya abonaste {formatCLP(getTotalAbonado(editCompromiso.id))}; lo pendiente se recalcula con el nuevo total.</span>
                )}
              </div>
              <div className="form-group">
                <label>Descripción (opcional)</label>
                <input type="text" value={formCompromiso.descripcion}
                  onChange={e => setFormCompromiso({ ...formCompromiso, descripcion: e.target.value })}
                  placeholder="Notas adicionales..." />
              </div>
              <div className="form-group">
                <label>Fecha compromiso</label>
                <input type="date" value={formCompromiso.fecha}
                  onChange={e => setFormCompromiso({ ...formCompromiso, fecha: e.target.value })} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={cerrarCompromiso}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Guardando...' : editCompromiso ? 'Guardar cambios' : 'Agregar compromiso'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Abono a compromiso */}
      {showAbono && (() => {
        const compromiso = compromisos.find(c => c.id === showAbono);
        if (!compromiso) return null;
        const pendiente = getPendienteCompromiso(compromiso);
        const bolsilloComp = bolsillos.find(b => b.id === compromiso.bolsillo_id);
        return (
          <div className="modal-overlay" onClick={() => setShowAbono(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Abonar a: {compromiso.nombre}</h3>
                <button className="modal-close" onClick={() => setShowAbono(null)}>×</button>
              </div>
              <div className="abono-info">
                <div className="abono-info-row">
                  <span>Total del compromiso:</span>
                  <strong>{formatCLP(compromiso.monto_total)}</strong>
                </div>
                <div className="abono-info-row">
                  <span>Ya abonado:</span>
                  <strong className="text-success">{formatCLP(getTotalAbonado(compromiso.id))}</strong>
                </div>
                <div className="abono-info-row">
                  <span>Pendiente:</span>
                  <strong className="text-warning">{formatCLP(pendiente)}</strong>
                </div>
              </div>
              <form onSubmit={handleAddAbono}>
                <div className="form-group">
                  <label>Monto a abonar ($)</label>
                  <input type="number" value={formAbono.monto}
                    onChange={e => setFormAbono({ ...formAbono, monto: e.target.value })}
                    placeholder="0" min="1" max={pendiente} required autoFocus />
                  <span className="field-hint">Máximo: {formatCLP(pendiente)}</span>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={formAbono.registrarSalida}
                      onChange={e => setFormAbono({ ...formAbono, registrarSalida: e.target.checked })}
                      style={{ width: 18, height: 18, accentColor: 'var(--primary)' }} />
                    Descontar el pago de "{bolsilloComp?.nombre || 'la categoría'}"
                  </label>
                  <span className="field-hint">
                    {formAbono.registrarSalida
                      ? 'Se registrará la salida de dinero automáticamente.'
                      : 'Solo se anota el abono; tendrás que registrar la salida de dinero por separado.'}
                  </span>
                </div>
                <div className="form-group">
                  <label>Nota (opcional)</label>
                  <input type="text" value={formAbono.nota}
                    onChange={e => setFormAbono({ ...formAbono, nota: e.target.value })}
                    placeholder="ej: Primera cuota, Pago parcial..." />
                </div>
                <div className="form-group">
                  <label>Fecha</label>
                  <input type="date" value={formAbono.fecha}
                    onChange={e => setFormAbono({ ...formAbono, fecha: e.target.value })} />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowAbono(null)}>Cancelar</button>
                  <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Registrar abono'}</button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Modal: Cuadrar con el banco */}
      {cuadrar && (() => {
        const bolsillo = bolsillos.find(b => b.id === cuadrar.bolsilloId);
        const saldoApp = fin.porBolsillo(cuadrar.bolsilloId).saldo;
        const tieneValor = cuadrar.saldoBanco !== '' && !Number.isNaN(parseFloat(cuadrar.saldoBanco));
        const diferencia = tieneValor ? Math.round(parseFloat(cuadrar.saldoBanco) - saldoApp) : 0;
        return (
          <div className="modal-overlay" onClick={() => setCuadrar(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Cuadrar {bolsillo?.nombre}</h3>
                <button className="modal-close" onClick={() => setCuadrar(null)}>×</button>
              </div>
              <p className="modal-description">
                Escribe el saldo que muestra tu banco hoy. La app registra un solo movimiento de ajuste
                para que el saldo quede igual. Tus compromisos y metas no cambian.
              </p>
              <form onSubmit={handleCuadrar}>
                <div className="form-group">
                  <label>Saldo según la app</label>
                  <input type="text" value={formatCLP(saldoApp)} disabled />
                </div>
                <div className="form-group">
                  <label>Saldo según tu banco hoy ($)</label>
                  <input type="number" inputMode="numeric" value={cuadrar.saldoBanco}
                    onChange={e => setCuadrar({ ...cuadrar, saldoBanco: e.target.value })}
                    placeholder="ej: 2733322" required autoFocus />
                </div>
                {tieneValor && (
                  <p className="field-hint" style={{ fontSize: '0.9rem' }}>
                    {diferencia === 0
                      ? '✅ Ya está cuadrado, no hace falta ajuste.'
                      : diferencia > 0
                        ? `Se registrará una entrada de ${formatCLP(diferencia)} ("Ajuste para cuadrar con el banco").`
                        : `Se registrará una salida de ${formatCLP(-diferencia)} ("Ajuste para cuadrar con el banco").`}
                  </p>
                )}
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setCuadrar(null)}>Cancelar</button>
                  <button type="submit" className="btn-primary" disabled={saving || !tieneValor}>
                    {saving ? 'Guardando...' : diferencia === 0 && tieneValor ? 'Cerrar' : 'Cuadrar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Lista de categorías */}
      {bolsillos.length === 0 ? (
        <div className="empty-bolsillos">
          <div className="empty-icon">📂</div>
          <h3>No tienes categorías todavía</h3>
          <p>Crea tu primera categoría para empezar a organizar tu dinero.</p>
          <button className="btn-primary" onClick={() => setShowNewBolsillo(true)}>
            Crear mi primera categoría
          </button>
        </div>
      ) : (
        <div className="bolsillos-grid">
          {[...bolsillos.filter(b => !b.aparte), ...bolsillos.filter(b => b.aparte)].map((bolsillo, i, lista) => {
            const { saldo, comprometido, enMetas, disponible } = fin.porBolsillo(bolsillo.id);
            const primeraAparte = bolsillo.aparte && !lista[i - 1]?.aparte;
            const compsBolsillo = compromisos.filter(c => c.bolsillo_id === bolsillo.id);
            const pendientes = compsBolsillo.filter(c => c.estado === 'pendiente');
            const pagados = compsBolsillo.filter(c => c.estado === 'pagado');
            const isExpanded = expandedBolsillo === bolsillo.id;
            const apartado = comprometido + enMetas;
            const porcentajeApartado = saldo > 0 ? Math.min(100, (apartado / saldo) * 100) : (apartado > 0 ? 100 : 0);
            const movsBolsillo = movimientos.filter(m => m.bolsillo_id === bolsillo.id);

            // Categoría aparte (ahorro): los montos quedan ocultos hasta que la abres
            const oculto = bolsillo.aparte && !isExpanded;
            const monto = (n) => (oculto ? '$ ••••' : formatCLP(n));

            return (
              <React.Fragment key={bolsillo.id}>
              {primeraAparte && (
                <div style={{ gridColumn: '1 / -1', marginTop: 12 }}>
                  <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem' }}>🔒 Aparte</h3>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    Tu ahorro. No se suma a tu saldo ni a tu disponible para gastar. Tócala para ver el monto.
                  </p>
                </div>
              )}
              <div className={`bolsillo-card ${isExpanded ? 'expanded' : ''}`}>
                <div className="bolsillo-header" style={{ borderTopColor: bolsillo.color }}
                  onClick={() => setExpandedBolsillo(isExpanded ? null : bolsillo.id)}>
                  <div className="bolsillo-title-row">
                    <div className="bolsillo-icon" style={{ background: bolsillo.color }}>
                      {getIconByTipo(bolsillo.tipo)}
                    </div>
                    <div className="bolsillo-title-info">
                      <h3>{bolsillo.nombre}</h3>
                      <span className="bolsillo-tipo">{TIPOS.find(t => t.value === bolsillo.tipo)?.label}</span>
                    </div>
                    <div className="bolsillo-expand-icon">{isExpanded ? '▲' : '▼'}</div>
                  </div>

                  <div className="bolsillo-amounts">
                    <div className="bolsillo-amount-item">
                      <span className="amount-label">Saldo total</span>
                      <span className="amount-value">{monto(saldo)}</span>
                    </div>
                    {!bolsillo.aparte && (
                      <>
                        <div className="bolsillo-amount-item">
                          <span className="amount-label">{enMetas > 0 ? 'Comprometido + metas' : 'Comprometido'}</span>
                          <span className="amount-value text-warning">{formatCLP(comprometido + enMetas)}</span>
                        </div>
                        <div className="bolsillo-amount-item highlight">
                          <span className="amount-label">Disponible real</span>
                          <span className={`amount-value large ${disponible >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatCLP(disponible)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {apartado > 0 && !oculto && (
                    <div className="bolsillo-progress">
                      <div className="progress-bar-bg">
                        <div className="progress-bar-fill"
                          style={{ width: `${porcentajeApartado}%`, background: disponible < 0 ? 'var(--danger)' : bolsillo.color }} />
                      </div>
                      <span className="progress-label-text">
                        {Math.round(porcentajeApartado)}% apartado{enMetas > 0 ? ' (compromisos + metas)' : ''}
                      </span>
                    </div>
                  )}
                </div>

                {isExpanded && (
                  <div className="bolsillo-body">
                    <div className="bolsillo-actions">
                      <button className="action-btn action-ingreso"
                        onClick={() => { setShowNewMovimiento(bolsillo.id); setFormMovimiento({ ...movimientoVacio(), tipo: 'ingreso', vincular: '' }); }}>
                        + Ingresar dinero
                      </button>
                      <button className="action-btn action-egreso"
                        onClick={() => { setShowNewMovimiento(bolsillo.id); setFormMovimiento({ ...movimientoVacio(), tipo: 'egreso' }); }}>
                        − Retirar dinero
                      </button>
                      <button className="action-btn action-compromiso"
                        onClick={() => setShowNewCompromiso(bolsillo.id)}>
                        📌 Nuevo compromiso
                      </button>
                      <button className="action-btn action-meta"
                        onClick={() => { setShowAbonarMeta(bolsillo.id); setFormAbonarMeta({ meta_id: '', monto: '' }); }}>
                        🎯 Abonar a Meta
                      </button>
                      <button className="action-btn action-cuadrar"
                        onClick={() => setCuadrar({ bolsilloId: bolsillo.id, saldoBanco: '' })}>
                        🏦 Cuadrar con el banco
                      </button>
                      <BotonAparte bolsillo={bolsillo} onCambiar={handleCambiarAparte} />
                      <button className="action-btn action-delete"
                        onClick={() => handleDeleteBolsillo(bolsillo.id)}>
                        🗑 Eliminar categoría
                      </button>
                    </div>

                    <div className="compromisos-section">
                      <h4>Compromisos pendientes ({pendientes.length})</h4>
                      {pendientes.length === 0 ? (
                        <p className="empty-text">No hay compromisos pendientes. ¡Todo el saldo está disponible!</p>
                      ) : (
                        <div className="compromisos-list">
                          {pendientes.map(comp => {
                            const abonado = getTotalAbonado(comp.id);
                            const pendienteComp = getPendienteCompromiso(comp);
                            const pct = comp.monto_total > 0 ? Math.round((abonado / comp.monto_total) * 100) : 0;
                            const abonosComp = abonos.filter(a => a.compromiso_id === comp.id);
                            return (
                              <div key={comp.id} className="compromiso-card">
                                <div className="compromiso-header">
                                  <div className="compromiso-info">
                                    <span className="compromiso-nombre">{comp.nombre}</span>
                                    {comp.descripcion && <span className="compromiso-desc">{comp.descripcion}</span>}
                                  </div>
                                  <div className="compromiso-actions">
                                    <button className="btn-abono" onClick={() => { setShowAbono(comp.id); setFormAbono(abonoVacio()); }}>Abonar</button>
                                    <button className="btn-icon-del" title="Editar" onClick={() => handleStartEditCompromiso(comp)}
                                      style={{ color: 'var(--primary)', fontSize: '0.9rem' }}>✎</button>
                                    <button className="btn-icon-del" onClick={() => handleDeleteCompromiso(comp.id)} title="Eliminar">×</button>
                                  </div>
                                </div>
                                <div className="compromiso-amounts">
                                  <span>Total: <strong>{formatCLP(comp.monto_total)}</strong></span>
                                  <span>Abonado: <strong className="text-success">{formatCLP(abonado)}</strong></span>
                                  <span>Pendiente: <strong className="text-warning">{formatCLP(pendienteComp)}</strong></span>
                                </div>
                                <div className="progress-bar-bg small">
                                  <div className="progress-bar-fill" style={{ width: `${pct}%`, background: '#10b981' }} />
                                </div>
                                <span className="progress-label-text">{pct}% abonado</span>
                                {abonosComp.length > 0 && (
                                  <div className="abonos-history-mini">
                                    <span className="abonos-history-title">Historial de abonos:</span>
                                    {abonosComp.map(abono => (
                                      <div key={abono.id} className="abono-item">
                                        <span>{formatFecha(abono.fecha)} — {formatCLP(abono.monto)}{abono.nota ? ` · ${abono.nota}` : ''}</span>
                                        <button className="btn-icon-del small" onClick={() => handleDeleteAbono(abono)}>×</button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {pagados.length > 0 && (
                      <div className="compromisos-section pagados">
                        <h4>Compromisos pagados ({pagados.length})</h4>
                        <div className="compromisos-list">
                          {pagados.map(comp => (
                            <div key={comp.id} className="compromiso-card pagado">
                              <div className="compromiso-header">
                                <span className="compromiso-nombre">✅ {comp.nombre}</span>
                                <div className="compromiso-actions">
                                  <span className="text-success">{formatCLP(comp.monto_total)}</span>
                                  <button className="btn-icon-del" title="Editar" onClick={() => handleStartEditCompromiso(comp)}
                                    style={{ color: 'var(--primary)', fontSize: '0.9rem' }}>✎</button>
                                  <button className="btn-icon-del" onClick={() => handleDeleteCompromiso(comp.id)}>×</button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {movsBolsillo.length > 0 && (
                      <div className="compromisos-section">
                        <h4>Historial de movimientos</h4>
                        <MovimientosPorMes
                          movimientos={movsBolsillo}
                          comprobantes={comprobantesMov}
                          onError={setError}
                          onEdit={handleStartEditMov}
                          onDelete={handleDeleteMovimiento}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Botón para separar una categoría del disponible (o volver a sumarla).
function BotonAparte({ bolsillo, onCambiar }) {
  return (
    <button className="action-btn"
      title={bolsillo.aparte ? 'Vuelve a sumarse a tu saldo y disponible' : 'Deja de sumarse a tu saldo y disponible'}
      onClick={() => onCambiar(bolsillo)}>
      {bolsillo.aparte ? '↩ Volver a sumarla al disponible' : '🔒 Dejarla aparte (ahorro)'}
    </button>
  );
}

export default Bolsillos;
